import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

export default function CreateClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", logo_path: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => setForm({ email: "", password: "", name: "", company: "", logo_path: "" });

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // No client_id yet (we're creating it) — logo will be stored temporarily
      // under 'branding/pending' and can be re-organised later. Using kind=branding
      // to still route to the /branding/ folder.
      const { data } = await api.post("/upload?kind=branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, logo_path: data.path }));
    } catch {
      toast.error("Erro a carregar logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/clients", { ...form, role: "client" });
      toast.success("Cliente adicionado à minha carteira");
      setOpen(false);
      reset();
      onCreated && onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] h-11 px-5 text-xs tracking-[0.2em] uppercase"
          data-testid="new-client-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Novo cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">Adicionar um cliente à minha carteira</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          {/* Client logo */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Logo do cliente</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-[#EAE3DB] bg-white flex items-center justify-center overflow-hidden">
                {form.logo_path ? (
                  <AuthImage path={form.logo_path} className="w-full h-full object-contain p-2" />
                ) : (
                  <span className="text-[10px] text-[#8B5E3C] uppercase tracking-widest">sem logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-9 px-4 text-xs uppercase tracking-widest"
                  data-testid="upload-client-logo-button"
                >
                  <Upload className="w-3.5 h-3.5 mr-2" />
                  {uploading ? "A enviar…" : (form.logo_path ? "Trocar" : "Carregar")}
                </Button>
                {form.logo_path && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, logo_path: "" })}
                    className="text-[10px] text-[#8A2A22] hover:underline uppercase tracking-widest"
                  >
                    Remover
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleLogo} />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nome</Label>
            <Input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="client-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Empresa</Label>
            <Input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="client-company-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Email</Label>
            <Input
              required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="client-email-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Palavra-passe</Label>
            <Input
              required type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 h-11 bg-white border-[#EAE3DB]"
              data-testid="client-password-input"
            />
          </div>
          <DialogFooter className="pt-4">
            <Button
              type="button" variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
              data-testid="client-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit" disabled={loading}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
              data-testid="client-save-button"
            >
              {loading ? "A guardar…" : "Criar cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
